import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useBuyPrice,
  useCurrentPrice,
  useIsExRoute,
  useSellPrice,
} from '../../../../recoil';
import { useParams } from 'react-router-dom';
import { useQrDepositeModal } from '../../../../recoil/hooks/useModalState';
import QrDepositeModalTailwind from '../../../../components/common/QrDepositeModalTailwind';
import { getSymbol } from '../../../../services/symbol.service';
import { SymbolDetails } from '../../../../types/symbol.types';
import {
  SpotAccountData,
  SpotAccountErrorResponse,
  SpotAccountSuccessResponse,
} from '../../../../types/funds.types';
import { getSpotAccountList } from '../../../../services/funds.service';
import {
  connectSocket,
  OrderData,
} from '../../../../services/websocket-client.service';
import { getCexEngineSocket } from '../../../../services/cex-engine-socket.service';
import './trade.css';
import LimitOrderForm from './LimitOrderForm';
import MarketOrderForm from './MarketOrderForm';
import { AccountDataService } from '../../services/AccountDataService';
import { useSnackbarMsg } from '../../../../hooks/snackbar';
import { useUserDetails } from '../../../../recoil/hooks/useUserState';
import { addOcoOrder, addStopOrder } from '../../../../services/order.service';
import type { OcoOrderData } from '../../../../types/order.types';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const MIN_PRICE_ORDER_IN_USD = 0.1; // Minimum order value in USD
const ORDER_VALUE_PRECISION = 3; // Decimal places for order value

const TradeForm = ({ isNotMobile = true }: { isNotMobile?: boolean }) => {
  const [user, setUser] = useUserDetails();
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [msg, setMsg] = useSnackbarMsg();
  const params = useParams<{ market: string }>();
  const [tradeType, setTradeType] = useState<'buy' | 'sell'>('buy');
  const [tradeSubType, setTradeSubType] = useState<
    'limit' | 'market' | 'stopLimit' | 'oco'
  >('limit');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [orderValue, setOrderValue] = useState('');
  const [sliderPercentage, setSliderPercentage] = useState(0);
  const [postOnly, setPostOnly] = useState(false);
  const [timeInForce, setTimeInForce] = useState<'GTC' | 'IOC' | 'FOK' | 'GTT'>(
    'GTC'
  );
  const [cancelAfter, setCancelAfter] = useState(60); // Default 60 seconds for GTT
  const [stopPrice, setStopPrice] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [qrDepositeModalOpen, setQrDepositeModalOpen] = useQrDepositeModal();
  const [baseCurrency, quoteCurrency] = params?.market
    ? params.market.split('-')
    : ['', ''];
  const [symbolDetails, setSymbolDetails] = useState<null | SymbolDetails>(
    null
  );
  const [errors, setErrors] = useState<{
    price?: string;
    quantity?: string;
    orderValue?: string;
    stopPrice?: string;
    limitPrice?: string;
    insufficientBalance?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isBalanceSocketConnected, setIsBalanceSocketConnected] =
    useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const socketRef = useRef<any>(null);
  const isExotic = useIsExRoute();

  useEffect(() => {
    if (user) setIsUserLoading(true);
  }, [user]);

  useEffect(() => {
    if (isUserLoading && user) {
      {
        setTimeout(() => {
          setIsUserLoading(false);
          setIsUserAuthenticated(user !== null);
        }, 500);
      }
    } else if (!isUserLoading && !user) setIsUserAuthenticated(false);
  }, [isUserLoading, user]);

  useEffect(() => {
    setTimeout(() => {
      setIsConnecting(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (isUserAuthenticated) {
      socketRef.current = isExotic ? getCexEngineSocket() : connectSocket();
      setTimeout(() => {
        socketRef.current?.ping();
      }, 1_000);
      const pingInt = setInterval(() => {
        socketRef.current?.ping();
      }, 18_000);

      return () => {
        socketRef.current?.socket?.disconnect();
        clearInterval(pingInt);
      };
    }
  }, [isUserAuthenticated, isExotic]);

  // Price in quote currency for base currency
  const currencyWorth = {
    [baseCurrency]: parseFloat(price),
    [quoteCurrency]: 1,
  };

  useEffect(() => {
    if (params?.market && !symbolDetails && isUserAuthenticated) {
      getSymbol(params?.market)
        .then((res) => {
          setSymbolDetails(res);
        })
        .catch((err) => {
          console.error('Error fetching symbol details:', err);
        });
    }
  }, [params?.market, isUserAuthenticated]);

  // Current Account Balance
  type SpotAccountResponse =
    | SpotAccountSuccessResponse
    | SpotAccountErrorResponse;

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const [accounts, setAccounts] = useState<{
    base: SpotAccountData | null;
    quote: SpotAccountData | null;
  }>({ base: null, quote: null });
  const [accountBalance, setAccountBalance] = useState<SpotAccountData | null>(
    null
  );
  const [isBaseBalanceLoading, setIsBaseBalanceLoading] = useState(false);
  const [isQuoteBalanceLoading, setIsQuoteBalanceLoading] = useState(false);

  // Fetch a single account with retry
  const fetchWithRetry = async (
    currency: string
  ): Promise<SpotAccountData | null> => {
    while (true) {
      try {
        const res = (await getSpotAccountList(currency)) as SpotAccountResponse;

        if (res?.success) {
          const account =
            res.data?.data?.find((acc) => acc.currency === currency) || null;
          return account;
        } else {
          console.warn(`Fetch failed for ${currency}. Retrying...`, res.error);
        }
      } catch (err) {
        console.error(`Error fetching ${currency}:`, err);
      }

      await sleep(30000);
    }
  };

  useEffect(() => {
    // Reset accounts and balance to show loader
    setAccounts({ base: null, quote: null });
    setAccountBalance(null);
    setIsBaseBalanceLoading(true);
    setIsQuoteBalanceLoading(true);

    if (isUserAuthenticated) {
      // Fetch base account
      if (baseCurrency) {
        fetchWithRetry(baseCurrency).then((baseAccount) => {
          setAccounts((prev) => ({ ...prev, base: baseAccount }));
          setIsBaseBalanceLoading(false);
          // If tradeType is 'sell', set balance immediately
          if (tradeType === 'sell') setAccountBalance(baseAccount);
        });
      }

      // Fetch quote account
      if (quoteCurrency) {
        fetchWithRetry(quoteCurrency).then((quoteAccount) => {
          setIsQuoteBalanceLoading(false);
          setAccounts((prev) => ({ ...prev, quote: quoteAccount }));

          // If tradeType is 'buy', set balance immediately
          if (tradeType === 'buy') setAccountBalance(quoteAccount);
        });
      }
    }
  }, [baseCurrency, quoteCurrency, isUserAuthenticated]);

  useEffect(() => {
    // Update balance if tradeType changes
    setAccountBalance(tradeType === 'buy' ? accounts.quote : accounts.base);
  }, [tradeType, accounts]);

  const service = new AccountDataService();

  useEffect(() => {
    let ws: WebSocket | null = null;

    const initWebSocket = async () => {
      ws = await service
        ?.connectAccountBalanceWebSocket((update) => {
          setAccounts((prev) => {
            const updated = { ...prev };

            if (prev.base && prev.base.currency === update.currency) {
              updated.base = {
                id: prev.base.id,
                currency: prev.base.currency,
                type: prev.base.type,
                balance: update.total,
                available: update.available,
                holds: update.hold,
              };
            }

            if (prev.quote && prev.quote.currency === update.currency) {
              updated.quote = {
                id: prev.quote.id,
                currency: prev.quote.currency,
                type: prev.quote.type,
                balance: update.total,
                available: update.available,
                holds: update.hold,
              };
            }

            setAccountBalance(
              tradeType === 'buy' ? updated.quote : updated.base
            );
            return updated;
          });
        })
        .catch((err) => {
          console.error('Failed to connect to account balance WS:', err);
          return null;
        });
    };

    if (isUserAuthenticated) initWebSocket();

    return () => ws?.close();
  }, [isUserAuthenticated]);

  useEffect(() => {
    if (isUserAuthenticated) {
      const interval = setInterval(() => {
        setIsBalanceSocketConnected(service.getIsBalanceSocketConnected());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isUserAuthenticated]);

  const [currentPrice] = useCurrentPrice();
  const baseCurrencyBalance = 0; // Replace with actual balance fetching logic
  const quoteCurrencyBalance = 0;

  // Function to format balance based on symbolDetails minSize
  const formatAvailableQty = (balance: string, currency: string): string => {
    if (!symbolDetails) return parseFloat(balance).toFixed(6);

    let minSize: string;
    if (currency === baseCurrency) {
      minSize = symbolDetails.baseMinSize;
    } else if (currency === quoteCurrency) {
      minSize = symbolDetails.quoteMinSize;
    } else {
      return parseFloat(balance).toFixed(6);
    }

    // Count decimal places in minSize
    const decimalPlaces = minSize?.split('.')[1]?.length || 0;
    // Add 2 more decimal places as requested
    const totalDecimalPlaces = decimalPlaces + 2;

    // Format the balance
    const balanceNum = parseFloat(balance);
    return balanceNum.toFixed(totalDecimalPlaces);
  };

  const availableQty = useMemo(() => {
    if (!accountBalance) return '0.000000';

    const currentCurr = tradeType === 'buy' ? quoteCurrency : baseCurrency;
    return formatAvailableQty(accountBalance.available, currentCurr);
  }, [accountBalance, tradeType, baseCurrency, quoteCurrency, symbolDetails]);

  useEffect(() => {
    // Reset form fields when trade type changes
    setPrice(currentPrice?.value ? currentPrice.value.toString() : '');
    setQuantity('');
    setOrderValue('');
    setSliderPercentage(0);
    setErrors({});
  }, [tradeType, params?.market]);

  const currentCurrency = useMemo(
    () => (tradeType === 'buy' ? quoteCurrency : baseCurrency),
    [tradeType, baseCurrency, quoteCurrency]
  );

  // TP/SL Validation functions
  const validateStopPrice = (value: string): string | undefined => {
    if (!value || parseFloat(value) <= 0) {
      return 'Stop price must be greater than 0';
    }

    if (tradeType === 'buy') {
      // For BUY orders: Stop Loss should be below current price
      const currentPriceValue = currentPrice?.value || parseFloat(price);
      if (currentPriceValue && parseFloat(value) >= currentPriceValue) {
        return 'Stop price must be below current price for buy orders';
      }
    } else {
      // For SELL orders: Stop Loss should be above current price
      const currentPriceValue = currentPrice?.value || parseFloat(price);
      if (currentPriceValue && parseFloat(value) <= currentPriceValue) {
        return 'Stop price must be above current price for sell orders';
      }
    }

    if (symbolDetails && symbolDetails.priceIncrement) {
      const increment = parseFloat(symbolDetails.priceIncrement);
      const priceValue = parseFloat(value);
      const remainder = priceValue % increment;
      if (
        Math.abs(remainder) > 1e-10 &&
        Math.abs(remainder - increment) > 1e-10
      ) {
        return `Stop price must be in increments of ${increment}`;
      }
    }

    return undefined;
  };

  const validateLimitPrice = (value: string): string | undefined => {
    if (!value || parseFloat(value) <= 0) {
      return 'Limit price must be greater than 0';
    }

    if (tradeType === 'buy') {
      // For BUY orders: Take Profit should be above current price
      const currentPriceValue = currentPrice?.value || parseFloat(price);
      if (currentPriceValue && parseFloat(value) <= currentPriceValue) {
        return 'Limit price must be above current price for buy orders';
      }
    } else {
      // For SELL orders: Take Profit should be below current price
      const currentPriceValue = currentPrice?.value || parseFloat(price);
      if (currentPriceValue && parseFloat(value) >= currentPriceValue) {
        return 'Limit price must be below current price for sell orders';
      }
    }

    if (symbolDetails && symbolDetails.priceIncrement) {
      const increment = parseFloat(symbolDetails.priceIncrement);
      const priceValue = parseFloat(value);
      const remainder = priceValue % increment;
      if (
        Math.abs(remainder) > 1e-10 &&
        Math.abs(remainder - increment) > 1e-10
      ) {
        return `Limit price must be in increments of ${increment}`;
      }
    }

    return undefined;
  };

  // Validation functions
  const validatePrice = (value: string): string | undefined => {
    if (!value || parseFloat(value) <= 0) {
      return 'Price must be greater than 0';
    }
    if (symbolDetails && symbolDetails.priceIncrement) {
      const increment = parseFloat(symbolDetails.priceIncrement);
      const priceValue = parseFloat(value);
      const remainder = priceValue % increment;
      // Check if remainder is significant (greater than a very small threshold)
      if (
        Math.abs(remainder) > 1e-10 &&
        Math.abs(remainder - increment) > 1e-10
      ) {
        return `Price must be in increments of ${increment}`;
      }
    }
    return undefined;
  };

  const validateQuantity = useCallback(
    (value: string): string | undefined => {
      if (!value || parseFloat(value) <= 0) {
        return 'Quantity must be greater than 0';
      }

      // For buy mode: check if the USD value of quantity exceeds available quote currency
      if (
        tradeType === 'buy' &&
        price &&
        currencyWorth &&
        currencyWorth[quoteCurrency]
      ) {
        const quantityValue = parseFloat(value);
        const priceValue = parseFloat(price);
        const orderValueInUSD = quantityValue * priceValue;
        const availableQuoteCurrency = parseFloat(availableQty);
        const availableQuoteInUSD =
          availableQuoteCurrency * currencyWorth[quoteCurrency];

        if (orderValueInUSD > availableQuoteInUSD) {
          const maxQuantity = availableQuoteInUSD / priceValue;
          return `Quantity exceeds available balance. Max: ${maxQuantity.toFixed(
            8
          )} ${baseCurrency}`;
        }
      } else {
        // For sell mode: check if quantity exceeds available base currency balance
        const availableBalance = parseFloat(availableQty);
        const quantityValue = parseFloat(value);
        if (quantityValue > availableBalance) {
          return `Quantity exceeds available balance (${availableQty})`;
        }
      }

      if (symbolDetails && symbolDetails.baseIncrement) {
        const increment = parseFloat(symbolDetails.baseIncrement);
        const quantityValue = parseFloat(value);
        const remainder = quantityValue % increment;
        // Check if remainder is significant (greater than a very small threshold)
        if (
          Math.abs(remainder) > 1e-10 &&
          Math.abs(remainder - increment) > 1e-10
        ) {
          return `Quantity must be in increments of ${increment}`;
        }
      }
      return undefined;
    },
    [
      tradeType,
      price,
      currencyWorth,
      quoteCurrency,
      availableQty,
      baseCurrency,
      symbolDetails,
    ]
  );

  const getMinimumOrderValue = useCallback((): number => {
    if (!symbolDetails) return MIN_PRICE_ORDER_IN_USD;

    // recreate currencyWorth locally to avoid using a changing object ref
    const currencyWorthLocal: Record<string, number> = {
      [baseCurrency]: parseFloat(price || '0'),
      [quoteCurrency]: 1,
    };

    const minOrderValueFromSymbol = symbolDetails.minFunds
      ? parseFloat(symbolDetails.minFunds)
      : MIN_PRICE_ORDER_IN_USD;

    if (tradeType === 'buy') {
      const quoteMinSize = parseFloat(symbolDetails.quoteMinSize || '0');
      const quoteCurrencyWorthInUSD = currencyWorthLocal[quoteCurrency] || 1;
      const minFromQuoteMinSize = quoteMinSize * quoteCurrencyWorthInUSD;
      return Math.max(
        MIN_PRICE_ORDER_IN_USD,
        minOrderValueFromSymbol,
        minFromQuoteMinSize
      );
    } else {
      const baseMinSize = parseFloat(symbolDetails.baseMinSize || '0');
      const baseCurrencyWorthInUSD = currencyWorthLocal[baseCurrency] || 1;
      const minFromBaseMinSize = baseMinSize * baseCurrencyWorthInUSD;
      return Math.max(
        MIN_PRICE_ORDER_IN_USD,
        minOrderValueFromSymbol,
        minFromBaseMinSize
      );
    }
  }, [symbolDetails, tradeType, quoteCurrency, baseCurrency, price]);

  const validateOrderValue = useCallback(
    (value: string): string | undefined => {
      if (!value || parseFloat(value) <= 0) {
        return 'Order value must be greater than 0';
      }

      // Special handling for market sell mode - use baseMinSize directly
      if (tradeSubType === 'market' && tradeType === 'sell' && symbolDetails) {
        const baseMinSize = parseFloat(symbolDetails.baseMinSize);
        const orderValueNum = parseFloat(value);

        if (orderValueNum < baseMinSize) {
          return `Minimum order value is ${baseMinSize} ${baseCurrency}`;
        }

        // Check if order value exceeds available balance
        const availableBalance = parseFloat(availableQty);
        if (orderValueNum > availableBalance) {
          return `Order value exceeds available balance (${availableBalance} ${baseCurrency})`;
        }

        return undefined;
      }

      // For limit sell mode: check against baseMinSize * current price
      if (
        tradeSubType === 'limit' &&
        tradeType === 'sell' &&
        symbolDetails &&
        price
      ) {
        const currentPriceValue = parseFloat(price);
        const orderValueInUSD = parseFloat(value);

        // Check if order value exceeds available balance
        const availableBalance = parseFloat(availableQty);
        const availableBalanceInUSD = availableBalance * currentPriceValue;
        if (orderValueInUSD > availableBalanceInUSD) {
          return `Order value exceeds available balance (${availableBalanceInUSD.toFixed(
            6
          )} ${quoteCurrency})`;
        }

        return undefined;
      }

      // For other modes (limit buy, market buy), use the original USD-based validation
      const minOrderValue = getMinimumOrderValue();
      if (parseFloat(value) < minOrderValue) {
        return `Minimum order value is ${minOrderValue.toFixed(
          6
        )} ${quoteCurrency}`;
      }

      // Check if order value exceeds available balance for buy orders
      if (
        tradeType === 'buy' &&
        accountBalance &&
        currencyWorth &&
        currencyWorth[quoteCurrency]
      ) {
        const availableQuoteCurrency = parseFloat(accountBalance.available);
        const availableQuoteInUSD =
          availableQuoteCurrency * currencyWorth[quoteCurrency];
        const orderValueInUSD = parseFloat(value);

        if (orderValueInUSD > availableQuoteInUSD) {
          return `Order value exceeds available balance (${availableQuoteInUSD.toFixed(
            6
          )} ${quoteCurrency})`;
        }
      }

      return undefined;
    },
    [
      tradeSubType,
      tradeType,
      symbolDetails,
      baseCurrency,
      availableQty,
      price,
      quoteCurrency,
      accountBalance,
      currencyWorth,
    ]
  );

  // TP/SL Handle functions
  const handleStopPriceChange = (value: string) => {
    setStopPrice(value);
    const error = validateStopPrice(value);
    setErrors((prev) => ({ ...prev, stopPrice: error }));
  };

  const handleLimitPriceChange = (value: string) => {
    setLimitPrice(value);
    const error = validateLimitPrice(value);
    setErrors((prev) => ({ ...prev, limitPrice: error }));
  };

  // Handle functions
  const handlePriceChange = (value: string) => {
    setPrice(value);
    const error = validatePrice(value);
    setErrors((prev) => ({ ...prev, price: error }));

    // Update quantity based on order value if order value exists
    if (orderValue && value && parseFloat(value) > 0) {
      const newQuantity = (parseFloat(orderValue) / parseFloat(value)).toFixed(
        8
      );
      setQuantity(newQuantity);
      const quantityError = validateQuantity(newQuantity);
      setErrors((prev) => ({ ...prev, quantity: quantityError }));
    }
  };

  const handleQuantityChange = (value: string) => {
    setQuantity(value);
    const error = validateQuantity(value);
    setErrors((prev) => ({ ...prev, quantity: error }));

    // Update order value based on price and quantity
    if (price && value && parseFloat(value) > 0 && parseFloat(price) > 0) {
      const newOrderValue = (parseFloat(value) * parseFloat(price)).toFixed(
        ORDER_VALUE_PRECISION
      );
      setOrderValue(newOrderValue);
      const orderValueError = validateOrderValue(newOrderValue);
      setErrors((prev) => ({ ...prev, orderValue: orderValueError }));
    }
  };

  const handleOrderValueChange = (value: string) => {
    setOrderValue(value);
    const error = validateOrderValue(value);
    setErrors((prev) => ({ ...prev, orderValue: error }));

    // Update quantity based on price and order value
    if (price && value && parseFloat(value) > 0 && parseFloat(price) > 0) {
      const newQuantity = (parseFloat(value) / parseFloat(price)).toFixed(8);
      setQuantity(newQuantity);
      const quantityError = validateQuantity(newQuantity);
      setErrors((prev) => ({ ...prev, quantity: quantityError }));
    }
  };

  const handleSliderChange = (percentage: number) => {
    setSliderPercentage(percentage);

    if (percentage === 0) {
      setQuantity('0');
      setOrderValue('0');
      setErrors((prev) => ({
        ...prev,
        quantity: undefined,
        orderValue: undefined,
      }));
      return;
    }

    if (tradeType === 'buy') {
      // For buy mode: calculate based on quote currency balance in USD
      if (!currencyWorth || !currencyWorth[quoteCurrency] || !price) return;

      const quoteCurrencyBalance = parseFloat(availableQty);
      const quoteCurrencyWorthInUSD =
        quoteCurrencyBalance * currencyWorth[quoteCurrency];

      // Calculate the USD value for this percentage
      const usdValue = (quoteCurrencyWorthInUSD * percentage) / 100;

      // Round to valid increment if needed (using quoteIncrement for USD value)
      let finalUsdValue = usdValue;
      if (symbolDetails && symbolDetails.quoteIncrement) {
        const increment = parseFloat(symbolDetails.quoteIncrement);
        finalUsdValue = Math.floor(usdValue / increment) * increment;
      }

      // Set order value in USD
      const usdValueStr = finalUsdValue.toFixed(ORDER_VALUE_PRECISION);
      setOrderValue(usdValueStr);

      // Calculate corresponding quantity
      const priceValue = parseFloat(price);
      if (priceValue > 0) {
        let newQuantity = finalUsdValue / priceValue;

        // Round quantity to baseIncrement for ETH (1e-7)
        if (symbolDetails && symbolDetails.baseIncrement) {
          const baseIncrement = parseFloat(symbolDetails.baseIncrement);
          newQuantity = Math.floor(newQuantity / baseIncrement) * baseIncrement;
        }

        const quantityStr = newQuantity.toFixed(8);
        setQuantity(quantityStr);

        // Validate
        const quantityError = validateQuantity(quantityStr);
        const orderValueError = validateOrderValue(usdValueStr);
        setErrors((prev) => ({
          ...prev,
          quantity: quantityError,
          orderValue: orderValueError,
        }));
      }
    } else {
      // For sell mode: calculate based on base currency balance
      const availableBalance = parseFloat(availableQty);
      let newQuantity = (availableBalance * percentage) / 100;

      // Round to the nearest valid increment (baseIncrement for base currency)
      if (symbolDetails && symbolDetails.baseIncrement) {
        const increment = parseFloat(symbolDetails.baseIncrement);
        newQuantity = Math.floor(newQuantity / increment) * increment;
      }

      const quantityStr = newQuantity.toFixed(8);
      setQuantity(quantityStr);

      // For market sell mode: show quantity as order value (BTC amount)
      if (tradeSubType === 'market') {
        setOrderValue(quantityStr);
        // Validate
        const quantityError = validateQuantity(quantityStr);
        const orderValueError = validateOrderValue(quantityStr);
        setErrors((prev) => ({
          ...prev,
          quantity: quantityError,
          orderValue: orderValueError,
        }));
      } else {
        // For limit sell mode: calculate USD value
        if (price && parseFloat(price) > 0) {
          const newOrderValue = (
            parseFloat(quantityStr) * parseFloat(price)
          ).toFixed(ORDER_VALUE_PRECISION);
          setOrderValue(newOrderValue);
          const quantityError = validateQuantity(quantityStr);
          const orderValueError = validateOrderValue(newOrderValue);
          setErrors((prev) => ({
            ...prev,
            quantity: quantityError,
            orderValue: orderValueError,
          }));
        }
      }
    }
  };

  const handleSubmitOrder = async () => {
    // Validate all fields
    const priceError = validatePrice(price);
    const quantityError = validateQuantity(quantity);
    const orderValueError = validateOrderValue(orderValue);

    let stopPriceError: string | undefined;
    let limitPriceError: string | undefined;

    // Validate stop price for stop-related orders
    if (tradeSubType === 'oco' || tradeSubType === 'stopLimit') {
      stopPriceError = validateStopPrice(stopPrice);
    }

    // Validate limit price for oco orders
    if (tradeSubType === 'oco') {
      limitPriceError = validateLimitPrice(limitPrice);
    }

    setErrors({
      price: priceError,
      quantity: quantityError,
      orderValue: orderValueError,
      stopPrice: stopPriceError,
      limitPrice: limitPriceError,
    });

    if (
      (priceError || quantityError || orderValueError) &&
      tradeSubType === 'limit'
    ) {
      return;
    }
    if (orderValueError && tradeSubType === 'market') {
      return;
    }
    if (
      (priceError ||
        quantityError ||
        orderValueError ||
        stopPriceError ||
        limitPriceError) &&
      tradeSubType === 'oco'
    ) {
      return;
    }
    if (
      (priceError || quantityError || orderValueError || stopPriceError) &&
      tradeSubType === 'stopLimit'
    ) {
      return;
    }

    if (!symbolDetails || !params?.market) {
      setMsg({
        msg: 'Missing market data',
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let response;

      if (tradeSubType === 'oco') {
        // Use OCO Order for TP/SL
        const ocoOrderData: OcoOrderData = {
          side: tradeType,
          symbol: params.market,
          stopPrice: stopPrice,
          limitPrice: limitPrice,
          size: quantity,
          price: price,
        };

        response = await addOcoOrder(ocoOrderData);
      } else if (tradeSubType === 'stopLimit') {
        // Use Stop Limit Order
        const stopOrderData = {
          side: tradeType,
          symbol: params.market,
          type: 'limit',
          price: price,
          size: quantity,
          stop: 'loss',
          stopPrice: stopPrice,
        };

        response = await addStopOrder(stopOrderData);
      } else {
        // Use order socket based on isExotic flag
        const currentSocket = socketRef.current;
        if (!currentSocket?.addOrder) {
          setMsg({
            msg: 'WebSocket connection not available',
            type: 'error',
          });
          return;
        }

        let orderData: OrderData = {
          side: tradeType,
          symbol: params.market,
          type: tradeSubType,
          postOnly: postOnly,
        };
        if (tradeSubType === 'limit') {
          orderData.price = price;
          orderData.hidden = isHidden;
          orderData.size = quantity;
        }
        if (tradeSubType === 'market' && tradeType === 'sell') {
          orderData.size = orderValue; // For market orders, use 'funds' to specify the order value in quote currency
        } else if (tradeSubType === 'market' && tradeType === 'buy') {
          orderData.funds = orderValue; // For market orders, use 'funds' to specify the order value in quote currency
        }
        response = await currentSocket.addOrder(orderData);
      }

      if (!response.success && !isExotic) {
        throw new Error(response.error || 'Order placement failed');
      }

      // Reset form on success
      setQuantity('');
      setOrderValue('');
      setStopPrice('');
      setLimitPrice('');
      setSliderPercentage(0);
      setErrors({});
      setIsHidden(false);
      setPostOnly(false);

      // Show success message
      if (!isExotic)
        setMsg({
          msg: `Order placed successfully! Order ID: ${
            response?.orderId || response.data?.data?.orderId || 'N/A'
          }`,
          type: 'success',
        });
    } catch (error) {
      setMsg({
        msg: `Error placing order: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (
      currentPrice?.value &&
      currentPrice?.marketParam == params?.market &&
      price == ''
    )
      setPrice(currentPrice?.value?.toString());
  }, [currentPrice]);

  useEffect(() => {
    setPrice('');
    setIsHidden(false);
  }, [params?.market]);

  useEffect(() => {
    if (isUserAuthenticated) {
      const interval = setInterval(() => {
        const currentSocket = socketRef.current;
        if (currentSocket) {
          const isConnected = isExotic
            ? currentSocket.isConnected
              ? currentSocket.isConnected()
              : false
            : currentSocket.getKucoinStatus()?.status === 'connected' &&
              currentSocket.getSocketConnected();
          setIsSocketConnected(isConnected);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isUserAuthenticated, isExotic]);

  return (
    <div
      className={`relative px-3  font-sans pb-2 ${
        isNotMobile ? ' w-[300px]' : 'w-full'
      }`}
    >
      {qrDepositeModalOpen ? (
        <QrDepositeModalTailwind />
      ) : (
        <div className="p-4 bg-[#f1f1f1] dark:bg-cardColor text-[#121212] dark:!text-white rounded-lg space-y-4 h-full">
          {/* Toggle Buy/Sell */}
          <div className="flex  mb-4">
            <button
              className={`flex-1  hover:bg-green text-white py-[5px] rounded-l text-f12 font-medium 
                ${
                  tradeType === 'buy' ? 'bg-green' : 'dark:bg-black bg-gray-400'
                }`}
              onClick={() => setTradeType('buy')}
            >
              Buy
            </button>
            <button
              className={`flex-1  hover:bg-red text-white py-[5px] rounded-r text-f12 font-medium ${
                tradeType === 'sell' ? 'bg-red' : 'dark:bg-black bg-gray-400'
              }`}
              onClick={() => setTradeType('sell')}
            >
              Sell
            </button>
          </div>

          {/* Tab Selection */}
          <div className="flex space-x-1 pb-2 text-sm">
            <span
              className={`px-3 pb-1 cursor-pointer ${
                tradeSubType === 'limit'
                  ? 'dark:text-[#b5ff00]  font-bold border-b-2 border-brand'
                  : 'dark:hover:text-gray-300  text-gray-400'
              }`}
              onClick={() => setTradeSubType('limit')}
            >
              Limit
            </span>
            <span
              className={`px-3 pb-1 cursor-pointer ${
                tradeSubType === 'market'
                  ? 'dark:text-[#b5ff00]  font-bold border-b-2 border-brand'
                  : 'dark:hover:text-gray-300  text-gray-400'
              }`}
              onClick={() => setTradeSubType('market')}
            >
              Market
            </span>
            {/* <span
              className={`px-3 pb-1 cursor-pointer ${tradeSubType === 'oco'
                ? 'text-[#facb3e] border-b-2 border-yellow-500'
                : 'hover:text-gray-300 text-gray-400'
                }`}
              onClick={() => setTradeSubType('oco')}
            >
              OCO
            </span>
            <span
              className={`px-3 pb-1 cursor-pointer ${tradeSubType === 'stopLimit'
                ? 'text-[#facb3e] border-b-2 border-yellow-500'
                : 'hover:text-gray-300 text-gray-400'
                }`}
              onClick={() => setTradeSubType('stopLimit')}
            >
              Stop Limit
            </span> */}
          </div>

          {/* Available Balance */}
          <div className="flex justify-between items-center pt-[10px] mb-2">
            <div className="text-f12 text-gray-400">Available Balance</div>
            <div className="text-sm font-mono flex items-center">
              {(
                tradeType == 'buy'
                  ? isQuoteBalanceLoading
                  : isBaseBalanceLoading
              ) ? (
                // Pulse loader placeholder
                <span className="loader-placeholder mr-1"></span>
              ) : availableQty && availableQty !== '0.000000' ? (
                <>
                  <span className="text-f16 font-bold mr-1">
                    {availableQty}
                  </span>
                  {currentCurrency}
                </>
              ) : (
                'N/A'
              )}
            </div>
          </div>

          {/* Conditional Order Form Rendering */}
          {(tradeSubType === 'limit' ||
            tradeSubType === 'oco' ||
            tradeSubType === 'stopLimit') && (
            <LimitOrderForm
              tradeType={tradeType}
              baseCurrency={baseCurrency}
              quoteCurrency={quoteCurrency}
              symbolDetails={symbolDetails}
              accountBalance={accountBalance}
              availableQty={availableQty}
              currentPrice={currentPrice}
              price={price}
              quantity={quantity}
              orderValue={orderValue}
              sliderPercentage={sliderPercentage}
              errors={errors}
              setPrice={setPrice}
              setQuantity={setQuantity}
              setOrderValue={setOrderValue}
              setSliderPercentage={setSliderPercentage}
              setErrors={setErrors}
              handlePriceChange={handlePriceChange}
              handleQuantityChange={handleQuantityChange}
              handleOrderValueChange={handleOrderValueChange}
              handleSliderChange={handleSliderChange}
              validatePrice={validatePrice}
              validateQuantity={validateQuantity}
              validateOrderValue={validateOrderValue}
              MIN_PRICE_ORDER_IN_USD={MIN_PRICE_ORDER_IN_USD}
              ORDER_VALUE_PRECISION={ORDER_VALUE_PRECISION}
            />
          )}

          {tradeSubType === 'market' && (
            <MarketOrderForm
              tradeType={tradeType}
              baseCurrency={baseCurrency}
              quoteCurrency={quoteCurrency}
              symbolDetails={symbolDetails}
              accountBalance={accountBalance}
              availableQty={availableQty}
              orderValue={orderValue}
              sliderPercentage={sliderPercentage}
              errors={errors}
              setOrderValue={setOrderValue}
              setSliderPercentage={setSliderPercentage}
              setErrors={setErrors}
              handleOrderValueChange={handleOrderValueChange}
              handleSliderChange={handleSliderChange}
              validateOrderValue={validateOrderValue}
              MIN_PRICE_ORDER_IN_USD={MIN_PRICE_ORDER_IN_USD}
              ORDER_VALUE_PRECISION={ORDER_VALUE_PRECISION}
            />
          )}

          <div className="flex justify-between items-center mt-5  rounded">
            <div className="text-f12 text-gray-400">Taker / Maker</div>
            <div className="text-sm font-mono">0.1% / 0.1%</div>
          </div>

          {/* Checkboxes */}
          {(tradeSubType === 'limit' || tradeSubType === 'market') &&
          !isExotic ? (
            <div className="flex items-center space-x-4 mb-4">
              <label className="flex items-center space-x-1 text-f12 gap-2 text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={postOnly}
                  onChange={() => setPostOnly(!postOnly)}
                  className="accent-yellow-500 outline-none cursor-pointer"
                />
                <span>Post-Only</span>
              </label>
              <label className="flex items-center space-x-1 text-f12 gap-2 text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isHidden}
                  onChange={() => setIsHidden(!isHidden)}
                  className="accent-yellow-500 outline-none cursor-pointer"
                />
                <span>Hidden</span>
              </label>
            </div>
          ) : null}
          {tradeSubType === 'oco' || tradeSubType === 'stopLimit' ? (
            <div className="relative mb-2">
              <label className="text-f12 text-gray-400">Stop Price</label>
              <input
                type="number"
                value={stopPrice}
                onChange={(e) => handleStopPriceChange(e.target.value)}
                className={`w-full dark:bg-black dark:text-white text-sm rounded px-3 py-2 mt-1 outline-none ${
                  errors.stopPrice ? 'border border-red-500' : ''
                }`}
                placeholder="0.00"
              />
              {errors.stopPrice && (
                <p className="text-red-500 text-f12 mt-1">{errors.stopPrice}</p>
              )}
            </div>
          ) : null}
          {/* Stop price and limit price section */}
          {tradeSubType === 'oco' && (
            <div className="relative mb-2">
              <label className="text-f12 text-gray-400">Limit Price</label>
              <input
                type="number"
                value={limitPrice}
                onChange={(e) => handleLimitPriceChange(e.target.value)}
                className={`w-full dark:bg-black dark:text-white text-sm rounded px-3 py-2 mt-1 outline-none ${
                  errors.limitPrice ? 'border border-red-500' : ''
                }`}
                placeholder="0.00"
              />
              {errors.limitPrice && (
                <p className="text-red-500 text-f12 mt-1">
                  {errors.limitPrice}
                </p>
              )}
            </div>
          )}

          {/* Time In Force Dropdown */}
          <div className="relative mb-4">
            <label className="text-f12 text-gray-400">Time In Force</label>
            <select
              value={timeInForce}
              onChange={(e) =>
                setTimeInForce(e.target.value as 'GTC' | 'IOC' | 'FOK' | 'GTT')
              }
              className="w-full dark:bg-black dark:text-white text-sm rounded px-3 py-2 mt-1 outline-none"
            >
              <option value="GTC">Good-Till-Canceled (GTC)</option>
              <option value="IOC">Immediate-Or-Cancel (IOC)</option>
              <option value="FOK">Fill-Or-Kill (FOK)</option>
              <option value="GTT">Good-Till-Time (GTT)</option>
            </select>
          </div>

          {/* GTT Time Input - Only show when GTT is selected */}
          {timeInForce === 'GTT' && (
            <div className="relative mb-4">
              <label className="text-f12 text-gray-400">
                Cancel After (seconds)
              </label>
              <input
                type="number"
                value={cancelAfter}
                onChange={(e) => setCancelAfter(parseInt(e.target.value) || 60)}
                min="1"
                max="86400" // 24 hours max
                className="w-full bg-black text-white text-sm rounded px-3 py-2 mt-1 outline-none"
                placeholder="60"
              />
              <div className="text-f12 text-gray-400 mt-1">
                Order will be cancelled after {cancelAfter} seconds
              </div>
            </div>
          )}

          {/* Buy/Sell Button */}
          <button
            className={`w-full text-white text-[12px] font-semibold py-3 rounded ${
              tradeType == 'buy'
                ? 'bg-green hover:bg-[#2ade8a]'
                : 'bg-red hover:bg-[#ff5157]'
            } ${
              isConnecting ||
              isSubmitting ||
              (tradeType == 'buy'
                ? accounts.quote === null
                : accounts.base === null) ||
              (tradeSubType !== 'oco' &&
                tradeSubType !== 'stopLimit' &&
                (!socketRef.current?.addOrder ||
                  !isSocketConnected ||
                  !isBalanceSocketConnected))
                ? 'opacity-50 !cursor-not-allowed'
                : ''
            }`}
            onClick={handleSubmitOrder}
            disabled={
              isConnecting ||
              isSubmitting ||
              (tradeType == 'buy'
                ? accounts.quote === null
                : accounts.base === null) ||
              (tradeSubType !== 'oco' &&
                tradeSubType !== 'stopLimit' &&
                (!socketRef.current?.addOrder ||
                  !isSocketConnected ||
                  !isBalanceSocketConnected))
            }
          >
            {isConnecting
              ? `Connecting...`
              : isSubmitting
              ? 'Processing...'
              : tradeType == 'buy'
              ? 'Buy'
              : 'Sell'}
          </button>

          {/* Deposit & Transfer Buttons */}
          <div className="flex space-x-4 mt-4 ">
            <button
              onClick={() => setQrDepositeModalOpen(true)}
              className="flex-1 dark:bg-black bg-gray-200 dark:hover:bg-gray-700 dark:text-white text-sm py-3 rounded"
            >
              Deposit
            </button>
            {/* <button className="flex-1 dark:bg-black bg-gray-200 dark:hover:bg-gray-700 dark:text-white  text-sm py-2 rounded">
              Transfer
            </button> */}
          </div>

          {/* Unified Trading Account Info */}
          <div className="text-xs text-gray-400 mt-4 space-y-2">
            <div className="flex items-center space-x-1">
              <span>Unified Trading Account</span>
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1 4v-4m0 0V9m0 7h.01"
                />
              </svg>
              <button className="text-[#7fb103] hover:underline text-xs">
                Buy Crypto
              </button>
            </div>

            <div className="flex justify-between">
              <span>{quoteCurrency} Balance</span>
              <span>
                {formatAvailableQty(
                  accounts?.quote?.available || '0',
                  quoteCurrency
                )}{' '}
                {quoteCurrency}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{baseCurrency} Balance</span>
              <span>
                {formatAvailableQty(
                  accounts?.base?.available || '0',
                  baseCurrency
                )}{' '}
                {baseCurrency}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Overlay for unauthenticated users */}
      {!isExotic && !isUserAuthenticated && (
        <div className="absolute inset-0 flex items-start justify-start rounded-lg px-3">
          <div className="dark:bg-black/70 bg-black/10 backdrop-blur-sm flex items-center justify-center rounded-lg h-[calc(100%-5px)] w-full">
            <div className="p-6 rounded-xl text-center">
              {isUserLoading ? (
                <>
                  <FontAwesomeIcon icon={faCircleNotch} spin={true} size="3x" />
                </>
              ) : (
                <>
                  <svg
                    className="w-[60px] h-[60px] text-gray-400 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Authentication Required
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Please log in to start trading and unlock all features
                  </p>

                  <button
                    className="bg-brand text-black font-bold py-2 text-sm px-6 rounded-lg w-full mb-2"
                    onClick={() => {
                      window.location.href = `${
                        import.meta.env.VITE_FE_BASE_URL
                      }#/login`;
                    }}
                  >
                    Log In to Trade
                  </button>
                  <p className="text-sm text-gray-500">
                    New user?{' '}
                    <span
                      className="text-brand cursor-pointer hover:underline"
                      onClick={() => {
                        window.location.href = `${
                          import.meta.env.VITE_FE_BASE_URL
                        }#/signup`;
                      }}
                    >
                      Create an account
                    </span>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* {isExotic && (
        <div className="absolute inset-0 flex items-start justify-start rounded-lg px-3">
          <div className='dark:bg-black/70 bg-black/10 backdrop-blur-sm flex items-center justify-center rounded-lg h-[calc(100%-5px)] w-full'>
            <div className="p-6 rounded-xl text-center">
              {
                isUserLoading ?
                  <>
                    <FontAwesomeIcon icon={faCircleNotch} spin={true} size='3x' />
                  </> :
                  <>
                    <svg
                      className="w-[60px] h-[60px] text-gray-400 mx-auto mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg><h3 className="text-lg font-semibold text-white mb-2">Inactive Market</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Trading is disabled for <b>Exotic</b> markets. Please select a different market.
                    </p>
                  </>
              }

            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default TradeForm;
